import { NavLink, Outlet, Navigate } from "react-router-dom";
import { useI18n, LANGS } from "../lib/i18n";
import { useStore } from "../lib/store";
import { useAuth } from "../lib/auth";
import { NotificationBell } from "./NotificationBell";
import {
  ShoppingCart,
  UtensilsCrossed,
  Package,
  BarChart3,
  Receipt,
  Truck,
  Globe,
  Settings,
  TrendingUp,
  Users,
  Ticket,
  Tags,
  LogOut,
} from "lucide-react";

const nav = [
  { to: "/kasir", label: "nav_kasir", icon: ShoppingCart, testid: "nav-kasir" },
  { to: "/meja", label: "nav_meja", icon: UtensilsCrossed, testid: "nav-meja" },
  { to: "/pesanan", label: "nav_online", icon: Globe, testid: "nav-pesanan" },
  { to: "/stok", label: "nav_stok", icon: Package, testid: "nav-stok" },
  { to: "/belanja", label: "nav_belanja", icon: Truck, testid: "nav-belanja", ownerOnly: true },
  { to: "/riwayat", label: "nav_riwayat", icon: Receipt, testid: "nav-riwayat" },
  { to: "/laporan", label: "nav_laporan", icon: BarChart3, testid: "nav-laporan", ownerOnly: true },
  { to: "/laba", label: "nav_laba", icon: TrendingUp, testid: "nav-laba", ownerOnly: true },
  { to: "/pelanggan", label: "nav_pelanggan", icon: Users, testid: "nav-pelanggan", ownerOnly: true },
  { to: "/promo", label: "nav_promo", icon: Ticket, testid: "nav-promo", ownerOnly: true },
  { to: "/label", label: "nav_label", icon: Tags, testid: "nav-label" },
  { to: "/pengaturan", label: "nav_settings", icon: Settings, testid: "nav-pengaturan", ownerOnly: true },
];

export const RequireOwner = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "owner") return <Navigate to="/kasir" replace />;
  return children;
};

export const Layout = () => {
  const { t, lang, setLang } = useI18n();
  const { settings } = useStore();
  const { user, ready, logout } = useAuth();

  if (!ready) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground">…</div>;
  }
  if (!user || !["owner", "cashier"].includes(user.role)) return <Navigate to="/login" replace />;
  const isOwner = user.role === "owner";
  const menu = nav.filter((n) => isOwner || !n.ownerOnly);

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row">
      <aside className="md:w-60 md:min-h-screen bg-white border-b md:border-b-0 md:border-r border-gray-200 md:sticky md:top-0 flex md:flex-col">
        <div className="flex md:hidden items-center gap-2 pl-3 py-2 shrink-0">
          <img
            src="/logo.png"
            alt="Kumamoto Halal Food"
            className="h-9 w-9 rounded-xl object-cover"
            data-testid="owner-logo-mobile"
          />
          {isOwner && <NotificationBell />}
        </div>
        <div className="hidden md:flex items-center gap-3 px-6 py-6">
          <div className="h-10 w-10 rounded-xl bg-primary grid place-items-center text-white overflow-hidden">
            <img
              src="/logo.png"
              alt="Kumamoto Halal Food"
              className="h-full w-full object-cover"
              data-testid="owner-logo"
            />
          </div>
          <div>
            <p className="font-display font-semibold leading-tight" data-testid="sidebar-store-name">
              {settings?.store_name?.split(" ")[0] || "Kumamoto"}
            </p>
            <p className="text-xs text-muted-foreground">{t("tagline")}</p>
          </div>
          <div className="ml-auto">
            {isOwner && <NotificationBell />}
          </div>
        </div>
        <nav className="flex md:flex-col gap-1 px-2 md:px-3 py-2 md:py-0 w-full overflow-x-auto">
          {menu.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 md:px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                  isActive
                    ? "bg-primary text-white shadow-sm"
                    : "text-gray-600 hover:bg-gray-100"
                }`
              }
            >
              <Icon size={18} />
              <span>{t(label)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="flex gap-1 px-2 md:px-3 py-2 md:mt-auto items-center">
          {LANGS.map((l) => (
            <button
              key={l.id}
              data-testid={`lang-${l.id}`}
              onClick={() => setLang(l.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors duration-200 ${
                lang === l.id
                  ? "bg-gray-900 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="hidden md:flex flex-col gap-1 px-3 pb-6 text-xs">
          <a
            href="/toko"
            data-testid="link-toko-online"
            className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium"
          >
            {t("toko_title")} →
          </a>
          <a
            href="/resto"
            data-testid="link-resto-online"
            className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium"
          >
            {t("resto_title")} →
          </a>
          <button
            data-testid="owner-logout-btn"
            onClick={logout}
            className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium flex items-center gap-2"
          >
            <LogOut size={14} /> {t("logout")}
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
};
